import math from '../../../util/math'
import { useSelector } from 'react-redux'
import { selectSnapshots } from '../../../selectors/security'
import { Snapshot } from '../../../reducer/security.reducer'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { getExpressionSecurityKey, getSecurityAsync, getSecurityKey, isFnOSecurity } from '../../../util/security'
import { calculateChange, toLocaleStringIn } from '../../../util/general'
import { convertExpressionToEvaluationExpression } from '../../../util/expression'
import { selectPrimarySecurity } from '../../../selectors/home'
import { ViewType } from '../../../assets/constants'
import { useStyles } from './styles'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { selectOptionsChainUnderlyingPrices } from '../../../selectors/options'
import { Box } from '@mui/material'
import SubtleShimmer from '../../../components/common/SubtleShimmer'

const LTPView = memo(({ width, view }: { width: number | undefined, view: ViewType }) => {
    const { classes } = useStyles()
    const location = useLocation()
    const { key, expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const selectedSecurity = useMemo(()=>expressionMode ? null : (primary && isFnOSecurity(primary) && (view !== ViewType.CHART && view !== ViewType.SYMBOL) ? underlying : primary) , [expressionMode, primary, underlying, view])
    const selectedSecurityKey = useMemo(()=>getSecurityKey(selectedSecurity?.exchange??0, selectedSecurity?.code??''), [selectedSecurity])
    const [ltp, setLTP] = useState<number | null>(null)
    const [ltpChange, setLTPChange] = useState<number>(0)
    const [ltpChangePercent, setLTPChangePercent] = useState<number>(0)
    const optionChainSnapShot = useSelector(selectOptionsChainUnderlyingPrices(selectedSecurityKey))
    const [previousCloseAvailable, setPreviousCloseAvailable] = useState<boolean>(false)
    const [hasData, setHasData] = useState<boolean>(false)
    const previousLTPRef = useRef(ltp)

    const snapshots: Record<string, Snapshot> = useSelector(selectSnapshots)
    const isExpectingData = !!key

    useEffect(() => {
        const run = async () => {
            if (!expressionMode) {
                setPreviousCloseAvailable(false)
                if (!key) {
                    setHasData(false)
                    return
                }
                const securityKey = selectedSecurity ? getSecurityKey(selectedSecurity.exchange, selectedSecurity.code) : key
                let selectedSecuritySnapshot = snapshots[securityKey]
                if(matchPath(ROUTES.simulator, location.pathname)) {
                    selectedSecuritySnapshot = {
                        ...selectedSecuritySnapshot,
                        lastTradedPrice: optionChainSnapShot?.underlyingCurrentPrice??0,
                        previousClose: optionChainSnapShot?.underlyingLastDayClosingPrice??(optionChainSnapShot?.underlyingCurrentPrice??0)
                    }
                }
                if (!selectedSecuritySnapshot) {
                    setHasData(false)
                    return
                }
                if(selectedSecuritySnapshot.previousClose === 0) {
                    setPreviousCloseAvailable(false)
                } else setPreviousCloseAvailable(true)
                const {
                    change,
                    changePercentage
                } = calculateChange(selectedSecuritySnapshot.lastTradedPrice, selectedSecuritySnapshot.previousClose)
                const priceMultiplier = selectedSecurity?.priceMultiplier || 100
                const newLtp = selectedSecuritySnapshot.lastTradedPrice / priceMultiplier
                if (ltp !== null && newLtp.toFixed(2) === ltp.toFixed(2)) return
                setLTP(selectedSecuritySnapshot.lastTradedPrice / priceMultiplier)
                setLTPChange(change)
                setLTPChangePercent(changePercentage)
                setHasData(true)
                return
            }
            if (!expressionSecurities?.length) {
                setHasData(false)
                return
            }
            setPreviousCloseAvailable(true)
            const {
                expression: evaluationExpression,
                context
            } = convertExpressionToEvaluationExpression(key, expressionSecurities)

            const derivedKeyMap: Record<string, string[]> = {}
            Object.keys(context).forEach((k) => {
                const s = context[k]
                if (!s.derived || k.indexOf('derived') === -1) return
                const secKey = getSecurityKey(s.exchange, s.underlyingSecurityCode!)
                if (!derivedKeyMap[secKey]) derivedKeyMap[secKey] = []
                derivedKeyMap[secKey].push(k)
            })

            const ltpMap: Record<string, any> = { ...context }
            // const change: Record<string, any> = {...context};
            const previousClose: Record<string, any> = { ...context }

            for (let c = 0; c < expressionSecurities.length; c++) {
                const sec = expressionSecurities[c]
                const token = getSecurityKey(sec.exchange, sec.code)
                const sn = snapshots[token]
                // const sec = securities[key]
                if (!sn || !sec) {
                    setHasData(false)
                    return
                }
                const expressionKey = getExpressionSecurityKey(sec.exchange, sec.code)
                const priceMultiplier = sec.priceMultiplier || 100
                // change[expressionKey] = sn.lastTradedPrice / priceMultiplier - sn.previousClose / priceMultiplier;
                ltpMap[expressionKey] = sn.lastTradedPrice / priceMultiplier
                previousClose[expressionKey] = sn.previousClose / priceMultiplier
                if (sn.referencedSecurity && sn.referencedSecurity.code) {
                    const refKey = getSecurityKey(sn.referencedSecurity.exchange, sn.referencedSecurity.code)
                    const referenceSecurity = await getSecurityAsync(refKey)
                    if (referenceSecurity) {
                        const underlyingKey =
                            getSecurityKey(referenceSecurity.exchange, referenceSecurity.underlyingSecurityCode!)
                        const derivedKeys = derivedKeyMap[underlyingKey] || []
                        derivedKeys.forEach((k) => {
                            ltpMap[k] = referenceSecurity
                            // change[k] = referenceSecurity
                            previousClose[k] = referenceSecurity
                        })
                    }
                }
            }
            const ltpResult: number = math.evaluate(evaluationExpression, ltpMap)
            const previousCloseResult: number = math.evaluate(evaluationExpression, previousClose)
            const changeResult = ltpResult - previousCloseResult

            const changePercentage = changeResult * 100 / Math.abs(previousCloseResult)

            if (ltpResult !== undefined && ltpResult.toFixed(2) !== previousLTPRef.current?.toFixed(2)) {
                previousLTPRef.current = ltpResult
                setLTP(ltpResult)
                setLTPChange(changeResult)
                setLTPChangePercent(changePercentage)
            }
            setHasData(true)
        }
        run()
    }, [key, snapshots, expressionMode, selectedSecurity, expressionSecurities, location.pathname, optionChainSnapShot])

    const hasValidExpressionData = expressionMode && (expressionSecurities?.length ?? 0) > 0
    const showShimmer = !key || (isExpectingData && (!hasData || (ltp === null && !hasValidExpressionData)))
    if (showShimmer) {
        return (
            <div className={classes.container}>
                <div className="security-ltp" style={{ display: 'flex', alignItems: 'baseline', gap: 4 , marginLeft:2}}>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 20, width: 72, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 12, width: 24, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 16, width: width && width <= 800 ? 48 : 100, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                </div>
            </div>
        )
    }

    return (<div className={classes.container}>
        <div className="security-ltp font-feature-num">
            <span
                style={{ fontSize: '1rem' ,marginLeft: '4px'}}>{toLocaleStringIn(ltp ?? 0)}</span>
            <span className="inr-tag" style={{ marginLeft: '4px', fontSize: '10px' }}>INR</span>

            {
                previousCloseAvailable ? (
                    (ltp !== null && ltp > 0 || expressionMode) &&
                    (
                        (width && width <= 800) ?
                            (
                                <span style={{
                                    fontSize: '14px',
                                    textAlign: 'right',
                                    marginLeft: '8px'
                                }}
                                    className={ltpChange > 0 ? 'green-text' : ltpChange < 0 ? 'red-text' : ''}>
                                    {`${ltpChangePercent > 0 ? '+' : ''}${ltpChangePercent.toFixed(2)}%`}
                                </span>
                            ) :
                            (
                                <span style={{
                                    fontSize: '14px',
                                    textAlign: 'right',
                                    marginLeft: '8px'
                                }}
                                    className={ltpChange > 0 ? 'green-text' : ltpChange < 0 ? 'red-text' : ''}>
                                    {`${ltpChange > 0 ? '+' : ''}${(expressionMode ? ltpChange : (ltpChange / (selectedSecurity?.priceMultiplier || 100))).toFixed(2)} (${ltpChangePercent > 0 ? '+' : ''}${ltpChangePercent.toFixed(2)}%)`}
                                </span>
                            )
                    )
                ) : null
            }
        </div>
    </div>)
})
LTPView.displayName = 'LtpView'
export default LTPView